import { useMemo } from 'react';

import { Icon } from './Icon';
import { tv } from './cn';

export const microphoneLevelVariant = tv(
  {
    slots: {
      base: 'flex items-center rounded-lg bg-bg-tertiary',
      iconWrapper: 'flex items-center justify-center text-text-primary',
      levelBarsWrapper: 'flex gap-2',
      levelBar:
        'h-[18px] w-2 rounded-full transition-colors duration-100 ease-in-out',
    },
    variants: {
      barStatus: {
        active: {
          levelBar: 'bg-bg-accent-strong-primary',
        },
        inactive: {
          levelBar: 'bg-border-secondary',
        },
      },
      variant: {
        default: {
          base: 'w-max flex-col gap-12 p-8 pt-10',
          iconWrapper:
            'rounded-full bg-bg-strong-secondary p-4 text-text-strong',
        },
        compact: {
          base: 'w-full justify-between gap-3 p-3',
          levelBar: 'h-[16px] w-1.5 rounded-full',
        },
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
  {
    responsiveVariants: true,
  },
);

export interface MicrophoneLevelVisualizationProps {
  waveformData?: number[];
  variant?: 'default' | 'compact';
  className?: string;
}

export const LEVEL_BAR_COUNT = {
  compact: 10,
  default: 20,
};

/**
 * Pure presentation component that displays audio level visualization from waveform data.
 * Accepts frequency data as a prop instead of directly analyzing a MediaStream.
 * Used by MicrophoneLevel for stream-based analysis, and directly in extension contexts
 * where the stream is in a different context (e.g., offscreen document).
 */
export const MicrophoneLevelVisualization: React.FC<
  MicrophoneLevelVisualizationProps
> = ({ waveformData, variant = 'default', className }) => {
  const { base, levelBarsWrapper, levelBar, iconWrapper } =
    microphoneLevelVariant({ variant });

  const barCount = useMemo(() => {
    if (!waveformData || waveformData.length === 0) {
      return 0;
    }

    // Calculate average intensity from waveform data (0-255 range)
    const avg =
      waveformData.reduce((sum, val) => sum + val, 0) / waveformData.length;

    // Convert to bar count (0 to LEVEL_BAR_COUNT)
    // Normalize from 0-255 to 0-LEVEL_BAR_COUNT
    const volumeLevel = Math.round(
      (avg * LEVEL_BAR_COUNT[variant]) / 64, // Dividing by 64 gives us a better visual range
    );

    return Math.min(volumeLevel, LEVEL_BAR_COUNT[variant]);
  }, [waveformData, variant]);

  return (
    <div
      data-accessibility-label="microphone-level"
      className={base({ className })}
    >
      <div className={iconWrapper()}>
        <Icon name="Mic" size={variant === 'compact' ? 'xs' : 'md'} />
      </div>
      <div className={levelBarsWrapper()}>
        {Array(LEVEL_BAR_COUNT[variant])
          .fill(0)
          .map((_, i) => (
            <div
              key={i}
              className={levelBar({
                barStatus: i + 1 < barCount ? 'active' : 'inactive',
              })}
            />
          ))}
      </div>
    </div>
  );
};
